import React from 'react';
import { useTheme } from '@material-ui/core/styles';
import { useMediaQuery } from '@material-ui/core';
import Divider from '@material-ui/core/Divider';
import { Fab } from '@material-ui/core';
import { styled } from '@material-ui/core/styles';
import { EstatusDeLineField, EstatusDeLineaButton } from './HomeStyles';
import ParoAutorizadoButton from '../../components/ParoAutorizado/ParoAutorizadoButton';


import {
    PlayerPlay,
    ClockStop,
    PlayerPause,
    HandStop,
    PlayerRecord,
    PlayerStop,
} from 'tabler-icons-react';
import Chart from 'react-apexcharts';


const ChartSectionContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    overflowY: 'hidden',
    overflowX: 'hidden',
    boxSizing: 'border-box',
    padding: '0',
    paddingLeft: '0',
    paddingRight: '0',
    marginBottom: '0px',
    marginRight: '0',
    justifyContent: 'flex-start',
    minWidth: 0,
    flexShrink: 1,
    minHeight: 0,
    maxHeight: '100%',
    flex: '1 1 auto',
    [theme.breakpoints.up('xs')]: {
        gap: '0.3cm',
        marginTop: '0.1vh',
    },
    [theme.breakpoints.up('sm')]: {
        gap: '0.6cm',
        marginTop: 'calc(-2.5vh + 1mm)',
    },
    [theme.breakpoints.up('md')]: {
        gap: '0.8cm',
        marginTop: 'calc(-2.5vh + 1mm)',
    },
    '@media (min-width: 1281px) and (max-width: 1917px)': {
        gap: '0.3cm',
        marginTop: '4vh',
    },
    '@media (min-width: 1923px)': {
        gap: '1%',
        marginTop: '1vh',
    },
    '@media (min-width: 1918px) and (max-width: 1922px) and (min-height: 966px) and (max-height: 970px)': {
        gap: '0.8cm',
    },
}));

const ChartWrapper = styled('div')(({ theme }) => ({
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    overflowY: 'hidden',
    overflowX: 'hidden',
    flexShrink: 0,
    position: 'relative',
    margin: '0',
    padding: '0',
    minWidth: 0,
    [theme.breakpoints.up('xs')]: {
        height: '50px',
        minHeight: '50px',
        maxHeight: '50px',
        marginBottom: '-7.5px',
    },
    [theme.breakpoints.up('sm')]: {
        height: '60px',
        minHeight: '60px',
        maxHeight: '60px',
        marginBottom: '-16.2px',
    },
    [theme.breakpoints.up('md')]: {
        height: '76px',
        minHeight: '76px',
        maxHeight: '76px',
        marginBottom: '-26px',
    },
    [theme.breakpoints.up('lg')]: {
        height: '76px',
        minHeight: '76px',
        maxHeight: '76px',
        marginBottom: '-14px',
    },
    [theme.breakpoints.up('xl')]: {
        height: '120px',
        minHeight: '120px',
        maxHeight: '120px',
        marginBottom: '-20px',
    },
}));

const ChartWrapperSecond = styled(ChartWrapper)(({ theme }) => ({
    marginBottom: '0',
    [theme.breakpoints.up('xs')]: {
        marginTop: '-7.5px',
    },
    [theme.breakpoints.up('sm')]: {
        marginTop: '-16.2px',
    },
    [theme.breakpoints.up('md')]: {
        marginTop: '-26px',
    },
    [theme.breakpoints.up('lg')]: {
        marginTop: '-14px',
    },

    [theme.breakpoints.up('xl')]: {
        marginTop: '-20px',
    },
}));

const allowRealCounts = process.env.REACT_APP_ALLOW_REAL_COUNTS !== 'false';

const ChartSection = React.memo(({ meta, cant, producido }) => {
    const theme = useTheme();
    const isXl = useMediaQuery(theme.breakpoints.up('xl'));
    const isLg = useMediaQuery(theme.breakpoints.up('lg'));
    const isMd = useMediaQuery(theme.breakpoints.up('md'));
    const isSm = useMediaQuery(theme.breakpoints.up('sm'));
    
    // Referencias para actualizar los charts de forma fluida
    const chartRealRef = React.useRef(null);
    const chartPlanRef = React.useRef(null);
    const prevProducidoRef = React.useRef(producido);
    const prevCantRef = React.useRef(cant);
    
    React.useEffect(() => {
        // Solo actualizar si el valor realmente cambió
        if (prevProducidoRef.current !== producido) {
            prevProducidoRef.current = producido;
            
            // Usar setTimeout para asegurar que el DOM esté listo
            const timeoutId = setTimeout(() => {
                // Actualizar el chart de forma fluida sin recrear el componente
                if (chartRealRef.current && chartRealRef.current.chart) {
                    try {
                        // Usar updateSeries con animación suave
                        chartRealRef.current.chart.updateSeries([{ data: [producido] }], true);
                    } catch (error) {
                        // Si falla, el componente se actualizará normalmente con las series
                    }
                }
            }, 0);
            
            return () => clearTimeout(timeoutId);
        }
    }, [producido]);
    
    React.useEffect(() => {
        // Solo actualizar si el valor realmente cambió
        if (prevCantRef.current !== cant) {
            prevCantRef.current = cant;
            
            // Usar setTimeout para asegurar que el DOM esté listo
            const timeoutId = setTimeout(() => {
                // Actualizar el chart de forma fluida sin recrear el componente
                if (chartPlanRef.current && chartPlanRef.current.chart) {
                    try {
                        // Usar updateSeries con animación suave
                        chartPlanRef.current.chart.updateSeries([{ data: [cant] }], true);
                    } catch (error) {
                        // Si falla, el componente se actualizará normalmente con las series
                    }
                }
            }, 0);
            
            return () => clearTimeout(timeoutId);
        }
    }, [cant]);
    
    const chartHeight = React.useMemo(() => {
        if (isXl) return 120;
        if (isLg) return 76;
        if (isMd) return 76;
        if (isSm) return 60;
        return 50;
    }, [isXl, isLg, isMd, isSm]);
    
    const barHeight = React.useMemo(() => {
        if (isXl) {
            return '50%';
        }
        if (isLg) {
            return '100%';
        }
        return '85%';
    }, [isLg, isXl]);
    
    const ideal = React.useMemo(() => [{ data: [cant] }], [cant]);
    const series1 = React.useMemo(() => [{ data: [producido] }], [producido]);

    const options = React.useMemo(() => ({
        chart: {
            type: 'bar',
            redrawOnParentResize: true,
            redrawOnWindowResize: true,
            toolbar: { show: false },
            offsetX: 0,
            offsetY: 0,
            animations: { 
                enabled: true,
                easing: 'easeinout',
                speed: 800,
                animateGradually: {
                    enabled: true,
                    delay: 150
                },
                dynamicAnimation: {
                    enabled: true,
                    speed: 800
                }
            },
            height: chartHeight,
            width: '100%',
            events: {
                dataPointSelection: undefined,
                dataPointMouseEnter: undefined,
                dataPointMouseLeave: undefined,
            }
        },
        tooltip: { enabled: false },
        colors: ['#472a7a'],
        grid: {
            padding: {
                top: 0,
                right: 0,
                bottom: 0,
                left: 15
            },
            show: false
        },
        plotOptions: {
            bar: { 
                borderRadius: 4, 
                horizontal: true, 
                barHeight: barHeight,
                dataLabels: { 
                    position: 'center',
                    hideOverflowingLabels: false
                },
                columnWidth: '100%',
                distributed: false,
                rangeBarOverlap: false,
                rangeBarGroupRows: false
            }
        },
        dataLabels: {
            enabled: true,
            formatter: function (val) {
                return val;
            },
            style: { 
                fontSize: '14px', 
                fontFamily: 'WorkSans', 
                fontWeight: 700, 
                colors: ['#FFFFFF'],
                dropShadow: {
                    enabled: true,
                    color: '#000000',
                    blur: 2,
                    opacity: 0.5
                }
            },
            offsetY: 0,
            offsetX: 0
        },
        xaxis: {
            categories: ['Plan'],
            min: 0,
            max: meta,
            labels: { show: false },
            axisTicks: {
                show: true,
            },
            axisBorder: {
                show: true,
            },
            floating: false,
            range: undefined
        },
        yaxis: {
            labels: { 
                style: { 
                    fontSize: '0.95rem',
                    fontFamily: 'WorkSans', 
                    fontWeight: 700,
                    colors: ['#13151C']
                },
                offsetX: 0,
                offsetY: 0,
                show: true,
                formatter: function(val) {
                    return val;
                }
            },
            floating: false,
            position: 'left'
        },
    }), [meta, chartHeight, barHeight]);

    const options1 = React.useMemo(() => ({
        chart: {
            type: 'bar',
            redrawOnParentResize: true,
            redrawOnWindowResize: true,
            toolbar: { show: false },
            offsetX: 0,
            offsetY: 0,
            animations: { 
                enabled: true,
                easing: 'easeinout',
                speed: 800,
                animateGradually: {
                    enabled: true,
                    delay: 150
                },
                dynamicAnimation: {
                    enabled: true,
                    speed: 800
                }
            },
            height: chartHeight,
            width: '100%',
            events: {
                dataPointSelection: undefined,
                dataPointMouseEnter: undefined,
                dataPointMouseLeave: undefined,
            }
        },
        tooltip: { enabled: false },
        colors: ['#00A375'],
        grid: {
            padding: {
                top: 0,
                right: 0,
                bottom: 0,
                left: 15
            },
            show: false
        },
        plotOptions: {
            bar: { 
                borderRadius: 4, 
                horizontal: true, 
                barHeight: barHeight,
                dataLabels: { 
                    position: 'center',
                    hideOverflowingLabels: false
                },
                columnWidth: '100%',
                distributed: false,
                rangeBarOverlap: false,
                rangeBarGroupRows: false
            }
        },
        dataLabels: {
            enabled: true,
            formatter: function (val) {
                return val;
            },
            style: { 
                fontSize: '14px', 
                fontFamily: 'WorkSans', 
                fontWeight: 700, 
                colors: ['#FFFFFF'],
                dropShadow: {
                    enabled: true,
                    color: '#000000',
                    blur: 2,
                    opacity: 0.5
                }
            },
            offsetY: 0,
            offsetX: 0
        },
        xaxis: {
            categories: ['Real'],
            min: 0,
            max: meta,
            labels: { show: false },
            axisTicks: {
                show: true,
            },
            axisBorder: {
                show: true,
            },
        },
        yaxis: {
            labels: { 
                style: { 
                    fontSize: '0.95rem',
                    fontFamily: 'WorkSans', 
                    fontWeight: 700,
                    colors: ['#13151C']
                },
                offsetX: 0,
                offsetY: 0,
                show: true,
                formatter: function(val) {
                    return val;
                }
            },
            floating: false,
            position: 'left'
        },
    }), [meta, chartHeight, barHeight]);

    return (
        <ChartSectionContainer
            className="chart-section-container"
        >
            <ChartWrapper>
                <Chart
                    ref={chartPlanRef}
                    key="plan-chart"
                    options={options}
                    series={ideal}
                    type="bar"
                    height={chartHeight}
                    width="100%"
                />
            </ChartWrapper>
            {allowRealCounts && (
                <ChartWrapperSecond>
                    <Chart
                        ref={chartRealRef}
                        key="real-chart"
                        options={options1}
                        series={series1}
                        type="bar"
                        height={chartHeight}
                        width="100%"
                    />
                </ChartWrapperSecond>
            )}
        </ChartSectionContainer>
    );
}, (prevProps, nextProps) => {
    // Solo re-renderizar si meta, cant o producido cambian
    return prevProps.meta === nextProps.meta && 
           prevProps.cant === nextProps.cant && 
           prevProps.producido === nextProps.producido;
});

const UpdateData = ({
    detailOrder,
    producido,
    selectOrder,
    cant,
    status,
    startCorrida,
    ordersF,
    isCorridaActive,
}) => {
    const theme = useTheme();
    const isLg = useMediaQuery(theme.breakpoints.up('lg'));

    const [is1920x968, setIs1920x968] = React.useState(false);
    
    React.useEffect(() => {
        const checkResolution = () => {
            setIs1920x968(window.innerWidth === 1920 && window.innerHeight === 968);
        };
        checkResolution();
        window.addEventListener('resize', checkResolution);
        return () => window.removeEventListener('resize', checkResolution);
    }, []);

    return (
        <div style={{
            width: '98%',
            marginLeft: '1%',
            marginRight: '1%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            gap: (!selectOrder && !isCorridaActive) ? '0' : '1.2vh',
            minHeight: 0,
            overflow: 'hidden',
            paddingTop: '0.5vh',
            paddingBottom: '0.5vh',
            flex: 1,
            height: '100%',
            boxSizing: 'border-box',
        }}>
            <EstatusDeLineField style={{ marginBottom: '0' }}>
                <div style={{ flexShrink: 0, marginTop: '1.5%', marginLeft: '2%' }}>
                    <h1 className="updateData-head-text">Estatus de la Línea</h1>
                </div>
                <ParoAutorizadoButton />
            </EstatusDeLineField>

            {(!!selectOrder || !!isCorridaActive) ? (
                <Divider className="generalData-divider" style={{ marginTop: is1920x968 ? '0.1vh' : (isLg ? '0.1vh' : '0.2vh'), marginBottom: '0' }} />
            ) : null}

            <div
                style={{
                    display: (!selectOrder || ordersF.length === 0) ? 'flex' : 'none',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    flex: 1,
                    height: 'auto',
                    minHeight: 0,
                    maxHeight: 'none',
                    marginTop: is1920x968 ? '0' : (isLg ? '0' : '3%'),
                    marginBottom: is1920x968 ? '0.15vh' : (isLg ? '0.2vh' : '1vh'),
                    paddingTop: '0',
                    paddingBottom: '0',
                    paddingLeft: '0',
                    paddingRight: '0',
                    textAlign: 'center',
                    boxSizing: 'border-box',
                    position: 'relative',
                    left: '0',
                    right: '0',
                    flexShrink: 0,
                    lineHeight: '0',
                }}
            >
                {(!selectOrder || ordersF.length === 0) ? (
                    <EstatusDeLineaButton
                        style={{
                            margin: '0 auto',
                            justifyContent: 'center',
                            maxWidth: 'none',
                            minWidth: 'auto',
                            width: 'auto',
                            flex: 'none'
                        }}
                    >
                        <Fab
                            className={`updateData-head-status-button5`}
                            disabled
                            variant="extended"
                            size="large"
                        >
                            <h2>
                                Fuera de operación
                            </h2>
                        </Fab>
                    </EstatusDeLineaButton>
                ) : null}
            </div>

            <div
                className="updateData-body"
                style={{
                    display: isCorridaActive || selectOrder ? 'flex' : 'none',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    maxHeight: '100%',
                    overflow: 'hidden',
                    width: '100%',
                    justifyContent: 'space-between',
                }}
            >
                <EstatusDeLineaButton
                    style={{
                        display: startCorrida ? 'flex' : 'none',
                        justifyContent: 'center',
                        marginBottom: isLg ? '0.3vh' : '0.5vh',
                        marginTop: isLg ? '0.3vh' : '0.5vh',
                        width: '100%',
                        marginLeft: '5%',
                        marginRight: '5%',
                    }}
                >
                    <Fab
                        className={`updateData-head-status-button${status.codigo}`}
                        disabled
                        variant="extended"
                        size="large"
                    >
                        <ClockStop
                            size={25}
                            strokeWidth={1}
                            color={'black'}
                            style={{
                                display: status.codigo === 0 ? 'flex' : 'none',
                            }}
                        />
                        <PlayerPlay
                            size={25}
                            strokeWidth={1}
                            color={'black'}
                            style={{
                                display: status.codigo === 1 ? 'flex' : 'none',
                            }}
                        />
                        <PlayerPause
                            size={25}
                            strokeWidth={1}
                            color={'black'}
                            style={{
                                display: status.codigo === 2 || status.codigo < 0 ? 'flex' : 'none',
                            }}
                        />
                        <HandStop
                            size={25}
                            strokeWidth={1}
                            color={'black'}
                            style={{
                                display: status.codigo === 3 ? 'flex' : 'none',
                            }}
                        />
                        <HandStop
                            size={25}
                            strokeWidth={1}
                            color={'white'}
                            style={{
                                display: status.codigo === 4 ? 'flex' : 'none',
                            }}
                        />
                        <PlayerRecord
                            size={25}
                            strokeWidth={1}
                            color={'black'}
                            style={{
                                display: status.codigo === 5 ? 'flex' : 'none',
                            }}
                        />
                        <PlayerStop
                            size={25}
                            strokeWidth={1}
                            color={'black'}
                            style={{
                                display: status.codigo >= 6 ? 'flex' : 'none',
                            }}
                        />

                        <h1 className={`updateData-head-status-button-text${status.codigo}`}>
                            {status.nombre}
                        </h1>
                    </Fab>
                </EstatusDeLineaButton>
                <div className="updateData-body-tittle">
                    <div className="updateData-body-tittle-block">
                        <h1>Meta planeada</h1>
                        <div className="updateData-body-text">
                            <h1 className="updateData-body-text-cantidad">{detailOrder.meta}</h1>
                        </div>
                    </div>
                    {allowRealCounts && (
                        <div className="updateData-body-tittle-block-prod">
                            <h1>Producido</h1>
                            <div className="updateData-body-text">
                                <h1 className="updateData-body-text-prod">{producido}</h1>
                            </div>
                        </div>
                    )}
                    {allowRealCounts && (
                        <div className="updateData-body-tittle-block">
                            <h1>Porcentaje</h1>
                            <div className="updateData-body-text">
                                <h1 className="updateData-body-text-porce">
                                    {((producido / (detailOrder.meta || 1)) * 100).toFixed(1)}
                                    {' %'}
                                </h1>
                            </div>
                        </div>
                    )}
                    {!allowRealCounts && (
                        <div className="updateData-body-tittle-block-prod">
                            <h1>Plan actual</h1>
                            <div className="updateData-body-text">
                                <h1 className="updateData-body-text-prod">{cant}</h1>
                            </div>
                        </div>
                    )}
                    {!allowRealCounts && (
                        <div className="updateData-body-tittle-block">
                            <h1>% Ideal</h1>
                            <div className="updateData-body-text">
                                <h1 className="updateData-body-text-porce">
                                    {((cant / (detailOrder.meta || 1)) * 100).toFixed(1)}
                                    {' %'}
                                </h1>
                            </div>
                        </div>
                    )}
                </div>

                <ChartSection
                    meta={detailOrder.meta}
                    cant={cant}
                    producido={producido}
                />
            </div>
        </div>
    );
};

export default UpdateData;
